import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  Card,
  CardContent,
  Skeleton,
  useTheme,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Login as LoginIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  FileDownload as ExportIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import axiosInstance from '../../services/axiosInstance';

// Types
interface AnalyticsOverview {
  period: { startDate: string; endDate: string };
  totalDownloads: number;
  totalSearches: number;
  totalLogins: number;
  totalPluginEvents: number;
  activeUsers: number;
  downloadsTrend: number;
  searchesTrend: number;
  loginsTrend: number;
}

interface TopAsset {
  assetId: string;
  title: string;
  fileName: string;
  downloadCount: number;
}

interface TopSearchTerm {
  term: string;
  count: number;
  avgResults: number;
}

interface UserActivity {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  downloadCount: number;
  searchCount: number;
  loginCount: number;
  pluginEvents: number;
  lastActive: string;
}

interface PluginSearchTerm {
  term: string;
  count: number;
}

interface PluginVersionInfo {
  version: string;
  count: number;
}

interface PluginUserInfo {
  email: string;
  firstName?: string;
  lastName?: string;
  eventCount: number;
}

interface EnhancedPluginStats {
  pluginType: 'indesign' | 'powerpoint';
  totalEvents: number;
  uniqueUsers: number;
  assetPlacements: number;
  searchCount: number;
  browseCount: number;
  downloadCount: number;
  topAssets: TopAsset[];
  topSearchTerms: PluginSearchTerm[];
  versionDistribution: PluginVersionInfo[];
  topUsers: PluginUserInfo[];
  topDocuments: { name: string; count: number }[];
}

interface TimelineDataPoint {
  date: string;
  downloads: number;
  searches: number;
  logins: number;
  pluginEvents: number;
}

interface UserOption {
  id: string;
  email: string;
  name: string;
}

interface StorageStats {
  totalSize: number;
  totalAssets: number;
  byFileType: { fileType: string; count: number; totalSize: number }[];
  largestFiles: { assetId: string; fileName: string; fileSize: number; fileType: string }[];
}

interface AssetLifecycleStats {
  staleAssets: { assetId: string; fileName: string; title: string; uploadedAt: string; fileType: string }[];
  recentUploads: number;
  avgVersionsPerAsset: number;
  neverDownloaded: number;
  totalArchived: number;
  totalTrashed: number;
}

interface SecurityStats {
  totalFailedLogins: number;
  failedByReason: { reason: string; count: number }[];
  topFailedIPs: { ip: string; count: number }[];
  topFailedEmails: { email: string; count: number }[];
  failedLoginsTrend: number;
}

type UserSortKey = 'downloadCount' | 'searchCount' | 'loginCount' | 'pluginEvents';

const PIE_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0097a7', '#c2185b', '#512da8', '#00796b', '#e64a19'];

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: number | string;
  trend?: number;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}> = ({ title, value, trend, icon, color, loading }) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 600, color }}>
                {typeof value === 'number' ? value.toLocaleString('sv-SE') : value}
              </Typography>
            )}
            {trend !== undefined && !loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {trend >= 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                )}
                <Typography
                  variant="caption"
                  sx={{ color: trend >= 0 ? 'success.main' : 'error.main' }}
                >
                  {trend >= 0 ? '+' : ''}{trend}% från förra perioden
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [customDateMode, setCustomDateMode] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [searchFilter, setSearchFilter] = useState<'all' | 'zero'>('all');
  
  // User table state
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(10);
  const [userSortBy, setUserSortBy] = useState<UserSortKey>('downloadCount');
  const [userSortDir, setUserSortDir] = useState<'asc' | 'desc'>('desc');

  // Data state
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [topAssets, setTopAssets] = useState<TopAsset[]>([]);
  const [topSearches, setTopSearches] = useState<TopSearchTerm[]>([]);
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [pluginStats, setPluginStats] = useState<EnhancedPluginStats[]>([]);
  const [timeline, setTimeline] = useState<TimelineDataPoint[]>([]);
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);
  const [lifecycleStats, setLifecycleStats] = useState<AssetLifecycleStats | null>(null);
  const [securityStats, setSecurityStats] = useState<SecurityStats | null>(null);

  // Fetch users for dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/analytics/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  const effectiveDays = useMemo(() => {
    if (!customDateMode || !customStart || !customEnd) return days;
    const start = new Date(customStart);
    const end = new Date(customEnd);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : days;
  }, [customDateMode, customStart, customEnd, days]);

  // Fetch all analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const params: Record<string, unknown> = { days: effectiveDays };
    if (selectedUserId) {
      params.userId = selectedUserId;
    }

    try {
      const [
        overviewRes,
        topAssetsRes,
        topSearchesRes,
        userActivityRes,
        pluginStatsRes,
        timelineRes,
        storageRes,
        lifecycleRes,
        securityRes,
      ] = await Promise.all([
        axiosInstance.get('/api/analytics/overview', { params }),
        axiosInstance.get('/api/analytics/top-assets', { params: { ...params, limit: 10 } }),
        axiosInstance.get('/api/analytics/top-searches', { params: { ...params, limit: 30 } }),
        selectedUserId ? Promise.resolve({ data: [] }) : axiosInstance.get('/api/analytics/user-activity', { params }),
        axiosInstance.get('/api/analytics/plugins-enhanced', { params }),
        axiosInstance.get('/api/analytics/timeline', { params }),
        axiosInstance.get('/api/analytics/storage'),
        axiosInstance.get('/api/analytics/lifecycle', { params: { days: effectiveDays } }),
        axiosInstance.get('/api/analytics/security', { params: { days: effectiveDays } }),
      ]);

      setOverview(overviewRes.data);
      setTopAssets(topAssetsRes.data);
      setTopSearches(topSearchesRes.data);
      setUserActivity(userActivityRes.data);
      setPluginStats(pluginStatsRes.data);
      setTimeline(timelineRes.data);
      setStorageStats(storageRes.data);
      setLifecycleStats(lifecycleRes.data);
      setSecurityStats(securityRes.data);
    } catch (err: unknown) {
      console.error('Failed to fetch analytics:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Kunde inte hämta analytics-data');
    } finally {
      setLoading(false);
    }
  }, [effectiveDays, selectedUserId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
  };

  // Get plugin display name
  const getPluginName = (type: string) => {
    switch (type) {
      case 'indesign': return 'InDesign';
      case 'powerpoint': return 'PowerPoint';
      default: return type;
    }
  };

  const filteredSearches = useMemo(() => {
    if (searchFilter === 'zero') return topSearches.filter((s) => s.avgResults === 0);
    return topSearches;
  }, [topSearches, searchFilter]);

  const sortedUserActivity = useMemo(() => {
    return [...userActivity].sort((a, b) => {
      const aVal = a[userSortBy];
      const bVal = b[userSortBy];
      return userSortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [userActivity, userSortBy, userSortDir]);

  const handleUserSort = (key: UserSortKey) => {
    if (userSortBy === key) {
      setUserSortDir(userSortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setUserSortBy(key);
      setUserSortDir('desc');
    }
  };

  // CSV Export
  const exportCSV = () => {
    const rows: string[][] = [];
    
    rows.push(['=== Översikt ===']);
    rows.push(['Nedladdningar', String(overview?.totalDownloads || 0)]);
    rows.push(['Sökningar', String(overview?.totalSearches || 0)]);
    rows.push(['Inloggningar', String(overview?.totalLogins || 0)]);
    rows.push(['Plugin-händelser', String(overview?.totalPluginEvents || 0)]);
    rows.push(['Aktiva användare', String(overview?.activeUsers || 0)]);
    rows.push([]);

    rows.push(['=== Mest nedladdade filer ===']);
    rows.push(['Fil', 'Nedladdningar']);
    topAssets.forEach((a) => rows.push([a.title || a.fileName, String(a.downloadCount)]));
    rows.push([]);

    rows.push(['=== Populära söktermer ===']);
    rows.push(['Sökterm', 'Antal', 'Snitt resultat']);
    topSearches.forEach((s) => rows.push([s.term, String(s.count), String(s.avgResults)]));
    rows.push([]);

    rows.push(['=== Mest aktiva användare ===']);
    rows.push(['Användare', 'Nedladdningar', 'Sökningar', 'Inloggningar', 'Plugin']);
    userActivity.forEach((u) => {
      const name = u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email;
      rows.push([name, String(u.downloadCount), String(u.searchCount), String(u.loginCount), String(u.pluginEvents)]);
    });

    const csvContent = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const failureReasonLabel = (reason: string): string => {
    switch (reason) {
      case 'invalid_password': return 'Fel lösenord';
      case 'user_not_found': return 'Okänd användare';
      case 'inactive_account': return 'Inaktivt konto';
      default: return reason;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Analytics Dashboard
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {!customDateMode ? (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tidsperiod</InputLabel>
              <Select
                value={days}
                label="Tidsperiod"
                onChange={(e) => setDays(Number(e.target.value))}
              >
                <MenuItem value={7}>Senaste 7 dagar</MenuItem>
                <MenuItem value={30}>Senaste 30 dagar</MenuItem>
                <MenuItem value={90}>Senaste 90 dagar</MenuItem>
                <MenuItem value={365}>Senaste året</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="date"
                label="Från"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="date"
                label="Till"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          )}
          <Button
            size="small"
            variant="text"
            onClick={() => setCustomDateMode(!customDateMode)}
          >
            {customDateMode ? 'Förinställda perioder' : 'Anpassat datumintervall'}
          </Button>
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Användare</InputLabel>
            <Select
              value={selectedUserId}
              label="Användare"
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <MenuItem value="">Alla användare</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name || user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Exportera som CSV">
            <Button size="small" variant="outlined" startIcon={<ExportIcon />} onClick={exportCSV} disabled={loading}>
              Export
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overview Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Nedladdningar"
            value={overview?.totalDownloads || 0}
            trend={overview?.downloadsTrend}
            icon={<DownloadIcon />}
            color={theme.palette.primary.main}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Sökningar"
            value={overview?.totalSearches || 0}
            trend={overview?.searchesTrend}
            icon={<SearchIcon />}
            color={theme.palette.info.main}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Inloggningar"
            value={overview?.totalLogins || 0}
            trend={overview?.loginsTrend}
            icon={<LoginIcon />}
            color={theme.palette.success.main}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Aktiva användare"
            value={overview?.activeUsers || 0}
            icon={<PeopleIcon />}
            color={theme.palette.warning.main}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Timeline Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Aktivitet över tid
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : timeline.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <RechartsTooltip 
                labelFormatter={(label: string) => new Date(label).toLocaleDateString('sv-SE')}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="downloads"
                name="Nedladdningar"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="searches"
                name="Sökningar"
                stroke={theme.palette.info.main}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="logins"
                name="Inloggningar"
                stroke={theme.palette.success.main}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="pluginEvents"
                name="Plugin-händelser"
                stroke={theme.palette.warning.main}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Ingen data för vald period
          </Typography>
        )}
      </Paper>

      <Grid container spacing={3}>
        {/* Top Assets */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Mest nedladdade filer
            </Typography>
            {loading ? (
              <Box>
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} height={40} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : topAssets.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Fil</TableCell>
                      <TableCell align="right">Nedladdningar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topAssets.map((asset, index) => (
                      <TableRow key={asset.assetId}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {asset.title || asset.fileName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={asset.downloadCount}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Inga nedladdningar under perioden
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Top Searches */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Söktermer
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={searchFilter}
                exclusive
                onChange={(_, val) => val && setSearchFilter(val)}
              >
                <ToggleButton value="all">Alla ({topSearches.length})</ToggleButton>
                <ToggleButton value="zero" color="error">
                  Utan resultat ({topSearches.filter((s) => s.avgResults === 0).length})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {loading ? (
              <Box>
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} height={40} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : filteredSearches.length > 0 ? (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Sökterm</TableCell>
                        <TableCell align="right">Antal</TableCell>
                        <TableCell align="right">Snitt resultat</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSearches.map((search, index) => (
                        <TableRow key={search.term}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {search.term}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{search.count}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={search.avgResults}
                              size="small"
                              color={search.avgResults === 0 ? 'error' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {searchFilter === 'zero' ? 'Inga sökningar utan resultat' : 'Inga sökningar under perioden'}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Enhanced Plugin Stats */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Plugin-användning
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="rectangular" width="50%" height={200} />
                <Skeleton variant="rectangular" width="50%" height={200} />
              </Box>
            ) : pluginStats.length > 0 ? (
              <Grid container spacing={3}>
                {pluginStats.map((plugin) => (
                  <Grid size={{ xs: 12, md: 6 }} key={plugin.pluginType}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {getPluginName(plugin.pluginType)}
                        </Typography>
                        <Typography variant="h4" sx={{ mb: 1 }}>
                          {plugin.totalEvents.toLocaleString('sv-SE')}
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            händelser
                          </Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                          <Chip label={`${plugin.uniqueUsers} användare`} size="small" variant="outlined" />
                          <Chip label={`${plugin.assetPlacements} placeringar`} size="small" color="primary" variant="outlined" />
                          {plugin.downloadCount > 0 && (
                            <Chip label={`${plugin.downloadCount} nedladdningar`} size="small" color="secondary" variant="outlined" />
                          )}
                          {plugin.searchCount > 0 && (
                            <Chip label={`${plugin.searchCount} sökningar`} size="small" color="info" variant="outlined" />
                          )}
                          {plugin.browseCount > 0 && (
                            <Chip label={`${plugin.browseCount} bläddring`} size="small" variant="outlined" />
                          )}
                        </Box>

                        {plugin.versionDistribution && plugin.versionDistribution.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Versioner:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {plugin.versionDistribution.map((v) => (
                                <Chip key={v.version} label={`v${v.version} (${v.count})`} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {plugin.topUsers && plugin.topUsers.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Mest aktiva användare:
                            </Typography>
                            {plugin.topUsers.map((u, idx) => (
                              <Typography key={u.email} variant="body2" sx={{ pl: 1 }}>
                                {idx + 1}. {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email} ({u.eventCount}x)
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {plugin.topDocuments && plugin.topDocuments.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Dokument som använder DAM:
                            </Typography>
                            {plugin.topDocuments.map((d, idx) => (
                              <Typography key={d.name} variant="body2" sx={{ pl: 1 }}>
                                {idx + 1}. {d.name} ({d.count}x)
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {plugin.topAssets && plugin.topAssets.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Mest placerade bilder:
                            </Typography>
                            {plugin.topAssets.slice(0, 5).map((asset, idx) => (
                              <Typography key={asset.assetId} variant="body2" sx={{ pl: 1 }}>
                                {idx + 1}. {asset.title || asset.fileName} ({asset.downloadCount}x)
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {plugin.topSearchTerms && plugin.topSearchTerms.length > 0 && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Sökningar i plugin:
                            </Typography>
                            {plugin.topSearchTerms.slice(0, 5).map((search, idx) => (
                              <Typography key={search.term} variant="body2" sx={{ pl: 1 }}>
                                {idx + 1}. &quot;{search.term}&quot; ({search.count}x)
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Ingen plugin-aktivitet under perioden
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* User Activity - full width, with pagination and sorting */}
        {!selectedUserId && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Mest aktiva användare
              </Typography>
              {loading ? (
                <Box>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} height={40} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : userActivity.length > 0 ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Användare</TableCell>
                          <TableCell align="right" sortDirection={userSortBy === 'downloadCount' ? userSortDir : false}>
                            <TableSortLabel
                              active={userSortBy === 'downloadCount'}
                              direction={userSortBy === 'downloadCount' ? userSortDir : 'desc'}
                              onClick={() => handleUserSort('downloadCount')}
                            >
                              Nedl.
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sortDirection={userSortBy === 'searchCount' ? userSortDir : false}>
                            <TableSortLabel
                              active={userSortBy === 'searchCount'}
                              direction={userSortBy === 'searchCount' ? userSortDir : 'desc'}
                              onClick={() => handleUserSort('searchCount')}
                            >
                              Sök
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sortDirection={userSortBy === 'loginCount' ? userSortDir : false}>
                            <TableSortLabel
                              active={userSortBy === 'loginCount'}
                              direction={userSortBy === 'loginCount' ? userSortDir : 'desc'}
                              onClick={() => handleUserSort('loginCount')}
                            >
                              Inlogg.
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sortDirection={userSortBy === 'pluginEvents' ? userSortDir : false}>
                            <TableSortLabel
                              active={userSortBy === 'pluginEvents'}
                              direction={userSortBy === 'pluginEvents' ? userSortDir : 'desc'}
                              onClick={() => handleUserSort('pluginEvents')}
                            >
                              Plugin
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedUserActivity
                          .slice(userPage * userRowsPerPage, userPage * userRowsPerPage + userRowsPerPage)
                          .map((user) => (
                          <TableRow key={user.userId}>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {user.firstName && user.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : user.email}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{user.downloadCount}</TableCell>
                            <TableCell align="right">{user.searchCount}</TableCell>
                            <TableCell align="right">{user.loginCount}</TableCell>
                            <TableCell align="right">{user.pluginEvents}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={sortedUserActivity.length}
                    page={userPage}
                    onPageChange={(_, p) => setUserPage(p)}
                    rowsPerPage={userRowsPerPage}
                    onRowsPerPageChange={(e) => { setUserRowsPerPage(parseInt(e.target.value, 10)); setUserPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Rader per sida:"
                  />
                </>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Ingen användaraktivitet under perioden
                </Typography>
              )}
            </Paper>
          </Grid>
        )}

        {/* Storage Analytics */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <StorageIcon color="action" />
              <Typography variant="h6">Lagring</Typography>
            </Box>
            {loading || !storageStats ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {formatBytes(storageStats.totalSize)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {storageStats.totalAssets.toLocaleString('sv-SE')} filer totalt
                  </Typography>
                </Box>

                {storageStats.byFileType.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                      Fördelning per filtyp:
                    </Typography>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={storageStats.byFileType.slice(0, 8)}
                          dataKey="totalSize"
                          nameKey="fileType"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          label={({ fileType, percent }: { fileType: string; percent: number }) =>
                            `${fileType} ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {storageStats.byFileType.slice(0, 8).map((_, idx) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value: number) => formatBytes(value)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                )}

                {storageStats.largestFiles.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Största filer:
                    </Typography>
                    {storageStats.largestFiles.slice(0, 5).map((f, idx) => (
                      <Box key={f.assetId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {idx + 1}. {f.fileName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatBytes(f.fileSize)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Asset Lifecycle */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <InventoryIcon color="action" />
              <Typography variant="h6">Tillgångslivscykel</Typography>
            </Box>
            {loading || !lifecycleStats ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">Nya uppladdningar</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{lifecycleStats.recentUploads}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">Aldrig nedladdade</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: lifecycleStats.neverDownloaded > 0 ? 'warning.main' : 'text.primary' }}>
                      {lifecycleStats.neverDownloaded}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">Snitt versioner/fil</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{lifecycleStats.avgVersionsPerAsset}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">Arkiverade / Papperskorg</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{lifecycleStats.totalArchived} / {lifecycleStats.totalTrashed}</Typography>
                  </Grid>
                </Grid>

                {lifecycleStats.staleAssets.length > 0 && (
                  <Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Oanvända filer (äldst, aldrig nedladdade):
                    </Typography>
                    {lifecycleStats.staleAssets.slice(0, 5).map((a, idx) => (
                      <Box key={a.assetId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {idx + 1}. {a.title || a.fileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(a.uploadedAt).toLocaleDateString('sv-SE')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Security Dashboard */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SecurityIcon color="action" />
              <Typography variant="h6">Säkerhet</Typography>
              {securityStats && securityStats.totalFailedLogins > 0 && (
                <Chip
                  label={`${securityStats.totalFailedLogins} misslyckade inloggningar`}
                  size="small"
                  color="error"
                  variant="outlined"
                  icon={<WarningIcon />}
                />
              )}
            </Box>
            {loading || !securityStats ? (
              <Skeleton variant="rectangular" height={100} />
            ) : securityStats.totalFailedLogins === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Inga misslyckade inloggningsförsök under perioden
              </Typography>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Orsak
                  </Typography>
                  {securityStats.failedByReason.map((r) => (
                    <Box key={r.reason} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{failureReasonLabel(r.reason)}</Typography>
                      <Chip label={r.count} size="small" color="error" variant="outlined" />
                    </Box>
                  ))}
                  {securityStats.failedLoginsTrend !== 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {securityStats.failedLoginsTrend > 0 ? (
                        <TrendingUpIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                      )}
                      <Typography variant="caption" sx={{ color: securityStats.failedLoginsTrend > 0 ? 'error.main' : 'success.main' }}>
                        {securityStats.failedLoginsTrend > 0 ? '+' : ''}{securityStats.failedLoginsTrend}% jmf förra perioden
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Mest drabbade e-poster
                  </Typography>
                  {securityStats.topFailedEmails.map((e) => (
                    <Box key={e.email} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{e.email}</Typography>
                      <Chip label={e.count} size="small" variant="outlined" />
                    </Box>
                  ))}
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    IP-adresser
                  </Typography>
                  {securityStats.topFailedIPs.map((ip) => (
                    <Box key={ip.ip} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ip.ip}</Typography>
                      <Chip label={ip.count} size="small" variant="outlined" />
                    </Box>
                  ))}
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsPage;
